import { DisbursementFormOptions } from "@/types/disbursements";
import { FormOption } from "@/types/form";
import moment from 'moment';
import { useDisbursementsStore } from '@/store/store/disbursementsStore';

export function formatCurrency(amount: number | null | undefined): string {
  if (!amount && amount !== 0) return '$0.00';
  return `$${amount.toLocaleString('en-US', { 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  })}`;
}

export function formatDate(date: string | Date | null | undefined): string {
  return formatDateWithPattern(date, 'MM/DD/YYYY', 'mm/dd/yyyy');
}

export function formatDateYYYYMMDD(date: string | Date | null | undefined): string {
  return formatDateWithPattern(date, 'YYYY-MM-DD', '');
}

function formatDateWithPattern(date: string | Date | null | undefined, pattern: string, defaultValue: string): string {
  if (!date) return defaultValue;
  
  const m = moment(date, [
    'MM/DD/YYYY',
    'YYYY-MM-DD',
    'YYYY-MM-DDTHH:mm:ss',
    'YYYY-MM-DDTHH:mm:ss.SSSZ',
    moment.ISO_8601
  ], true);
  
  if (!m.isValid()) return defaultValue;
  
  return m.format(pattern);
}

export function formatFieldName(fieldName: string): string {
  const fieldNameMap: Record<string, string> = {
    'disbursementAmount': 'Disb. Amount',
    'disbursementDate': 'Disb. Date',
    'disbursementStatusTypeId': 'Disb. Status',
    'disbursementMethodTypeId': 'Disb. Method',
    'certificationDisbursementStatusReasonTypeId': 'Status Reason',
  };
  
  if (fieldNameMap[fieldName]) {
    return fieldNameMap[fieldName];
  }
  
  // Fallback: Convert camelCase to Title Case
  const result = fieldName.replace(/([A-Z])/g, ' $1');
  return result.charAt(0).toUpperCase() + result.slice(1);
}

export function getStatusDisplayName(
  statusTypeId: number | undefined,
  statusOptions: FormOption[] | undefined
): string {
  if (!statusTypeId || !statusOptions) return '';
  const option = statusOptions.find(s => s.id === statusTypeId);
  return option?.label || statusTypeId.toString();
}

export function getReasonDisplayName(
  reasonTypeId: number | undefined,
  reasonOptions: FormOption[] | undefined
): string {
  if (!reasonTypeId || !reasonOptions) return '';
  const option = reasonOptions.find(r => r.id === reasonTypeId);
  return option?.label || reasonTypeId.toString();
}

export function getMethodDisplayName(
  methodTypeId: number | undefined,
  methodOptions: FormOption[] | undefined
): string {
  if (!methodTypeId || !methodOptions) return '';
  const option = methodOptions.find(m => m.id === methodTypeId);
  return option?.label || methodTypeId.toString();
}

export function formatValue(
  fieldName: string,
  value: any,
  formOptions: DisbursementFormOptions
): string {
  if (value === null || value === undefined) return '';
  
  // Status lookup
  if (fieldName === 'disbursementStatusTypeId') {
    return getStatusDisplayName(value, formOptions.disbursementStatusTypes);
  }
  
  // Method lookup
  if (fieldName === 'disbursementMethodTypeId') {
    return getMethodDisplayName(value, formOptions.disbursementMethodTypes);
  }
  
  // Currency fields
  if (fieldName.toLowerCase().includes('amount')) {
    return formatCurrency(value);
  }
  
  // Date fields
  if (fieldName.toLowerCase().includes('date')) {
    return formatDate(value);
  }
  
  // Default
  return value.toString();
}

export function calculateTotalDisbursementAmount(): number {
  const store = useDisbursementsStore();
  let total = 0;
  
  for (const disb of store.disbursements) {    
    const formAmount = store.getFormAmount(disb.details.id);    
    if (formAmount !== null) {      
      total += formAmount;
    } 
    else {      
      total += disb.details.disbursementAmount || 0;
    }
  }  
  return total;
}

export function parseDateString(dateStr: string): Date {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
}